import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MdArrowBack, 
  MdUpload, 
  MdFileUpload,
  MdCloudUpload,
  MdCheckCircle,
  MdError,
  MdWarning,
  MdInfo,
  MdSave,
  MdTableChart,
  MdVisibility,
  MdClose
} from 'react-icons/md';
import UsibrasLoader from '../../../components/UsibrasLoader';
import UsibrasToast from '../../../components/UsibrasToast';
import { tokenManager } from '../../../utils/tokenManager';

const AddManPeelingData = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [expectedWorkingDays, setExpectedWorkingDays] = useState();
  const [targetValue, setTargetValue] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showInvalidCardsModal, setShowInvalidCardsModal] = useState(false);
  const [showDuplicateDatesModal, setShowDuplicateDatesModal] = useState(false);
  const [invalidCards, setInvalidCards] = useState([]);
  const [duplicateDates, setDuplicateDates] = useState([]);

  // Show toast notification
  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 3000);
  };

  // Fetch current general target
  const fetchCurrentTarget = async () => {
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        console.error('No auth token found');
        return;
      }

      const response = await fetch('https://usibras-web-backend.onrender.com/api/general-manual-targets', {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        }
      });

      const result = await response.json();

      if (response.ok && result.data && result.data.length > 0) {
        // Get the most recent target (first item in the array)
        const mostRecentTarget = result.data[0];
        setTargetValue(parseFloat(mostRecentTarget.target_value));
      }
    } catch (error) {
      console.error('Error fetching current target:', error);
    }
  };

  // Fetch current target on component mount
  React.useEffect(() => {
    fetchCurrentTarget();
  }, []);

  // Clear uploaded data
  const handleClearData = () => {
    setCsvData([]);
    setHeaders([]);
    setShowPreview(false);
    setUploadedFile(null);
    setValidationErrors([]);
    setIsSubmitted(false);
    setUploadProgress(0);
    showToast('Data cleared successfully', 'info');
  };

  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!file) return;
    
    // Validate file type - accept .xlsx files
    if (!file.name.endsWith('.xlsx') && !file.type.includes('spreadsheetml')) {
      showToast('Please upload a valid Excel (.xlsx) file', 'error');
      return;
    }

    setUploadedFile(file);
    setIsSubmitted(false);
    
    // Parse the Excel file to show preview
    await parseExcelFile(file);
  };

  // Parse Excel file to show preview
  const parseExcelFile = async (file) => {
    try {
      const XLSX = await import('xlsx');
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          
          // Get first sheet
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length < 2) {
            showToast('Excel file must contain at least a header row and one data row', 'error');
            return;
          }
          
          // Extract headers (first row)
          const excelHeaders = jsonData[0].map(h => String(h).trim());
          
          // Extract data rows (skip header, take first 5 rows for preview)
          const dataRows = jsonData.slice(1, 6).map((row, index) => {
            const rowData = {};
            excelHeaders.forEach((header, headerIndex) => {
              rowData[header] = row[headerIndex] !== undefined && row[headerIndex] !== null ? String(row[headerIndex]) : '';
            });
            rowData._rowNumber = index + 2; // Row number in Excel (1-indexed + header)
            rowData._isValid = true;
            return rowData;
          });
          
          setCsvData(dataRows);
          setHeaders(excelHeaders);
          setShowPreview(true);
          setValidationErrors([]);
          
        } catch (error) {
          showToast('Error parsing Excel file: ' + error.message, 'error');
          console.error('Parse error:', error);
        }
      };
      
      reader.readAsBinaryString(file);
    } catch (error) {
      showToast('Error loading Excel parser: ' + error.message, 'error');
      console.error('Error:', error);
    }
  };

  // Handle submit - upload to API
  const handleSubmit = async () => {
    if (!uploadedFile) {
      showToast('Please upload a file first', 'error');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress animation
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Get authorization header
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        clearInterval(progressInterval);
        throw new Error('Authentication token not found. Please login again.');
      }

      // Create form data
      const formData = new FormData();
      formData.append('file', uploadedFile);

      // Send to API
      const response = await fetch('https://usibras-web-backend.onrender.com/api/manual-peeling/bulk', {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
        },
        body: formData
      });

      const data = await response.json();

      // Complete progress
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        setIsSubmitted(true);
        // Show success message from API
        const successMessage = data.message || 'Bulk upload completed successfully!';
        if (data.summary) {
          showToast(`${successMessage} - ${data.summary.successful} records processed${data.summary.failed > 0 ? `, ${data.summary.failed} failed` : ''}`, 'success');
        } else {
          showToast(successMessage, 'success');
        }
        
        // Navigate after 2 seconds
        setTimeout(() => {
          navigate('/admin/manualpeeling');
        }, 2000);
      } else {
        setIsSubmitted(false);
        
        // Handle invalid card numbers error specifically
        if (data.invalid_card_numbers && data.invalid_card_numbers.length > 0) {
          setInvalidCards(data.invalid_card_numbers);
          setShowInvalidCardsModal(true);
          showToast(`Found ${data.invalid_card_numbers.length} invalid card numbers. Please check the details.`, 'error');
        } 
        // Handle duplicate dates error specifically
        else if (data.duplicate_dates && data.duplicate_dates.length > 0) {
          setDuplicateDates(data.duplicate_dates);
          setShowDuplicateDatesModal(true);
          showToast(`Found ${data.duplicate_dates.length} duplicate dates. Please check the details.`, 'error');
        } 
        else {
          showToast(data.message || data.error || 'Failed to upload file', 'error');
        }
      }
    } catch (error) {
      setIsSubmitted(false);
      showToast('Error uploading file: ' + error.message, 'error');
      console.error('Error:', error);
    } finally {
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 1000);
    }
  };


  // Handle drag and drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };



  // Save monthly working days configuration
  const handleSaveWorkingDaysConfig = async () => {
    if (!selectedMonth || !expectedWorkingDays || expectedWorkingDays < 1 || expectedWorkingDays > 31) {
      showToast('Please enter valid month and working days (1-31)', 'error');
      return;
    }

    setIsLoading(true);
    try {
      // Get authorization header
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }

      // Parse month and year from selectedMonth (format: "YYYY-MM")
      const [year, month] = selectedMonth.split('-').map(Number);
      
      const response = await fetch('https://usibras-web-backend.onrender.com/api/monthly-working-days', {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          month: month,
          year: year,
          expected_working_days: expectedWorkingDays
        })
      });

      const data = await response.json();

      if (response.ok) {
        showToast(data.message || 'Expected working days configuration saved successfully!', 'success');
      } else {
        // Check if it's a duplicate entry error
        if (data.error && data.error.includes('already exists')) {
          showToast(data.error, 'warning');
        } else {
          showToast(data.message || data.error || 'Failed to save configuration', 'error');
        }
      }
    } catch (error) {
      showToast('Error saving configuration: ' + error.message, 'error');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Save general manual peeling target
  const handleSaveTarget = async () => {
    if (!targetValue || targetValue <= 0) {
      showToast('Please enter a valid target value greater than 0', 'error');
      return;
    }

    setIsLoading(true);
    try {
      // Get authorization header
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }
      
      const response = await fetch('https://usibras-web-backend.onrender.com/api/general-manual-targets', {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          target_value: parseFloat(targetValue)
        })
      });

      const data = await response.json();

      if (response.ok) {
        showToast(data.message || 'General manual peeling target added successfully!', 'success');
        // Refetch current target to get the latest
        await fetchCurrentTarget();
      } else {
        // Check if it's a duplicate entry error
        if (data.error && data.error.includes('already exists')) {
          showToast(data.error, 'warning');
        } else {
          showToast(data.message || data.error || 'Failed to save target', 'error');
        }
      }
    } catch (error) {
      showToast('Error saving target: ' + error.message, 'error');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/manualpeeling')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <MdArrowBack className="text-xl" />
              <span>Back to Manual Peeling</span>
            </button>
          </div>
          <h1 className="text-3xl font-bold text-gray-800">Import Manual Peeling Data</h1>
        </div>
        <p className="text-gray-600 mt-2">
          Upload Excel (.xlsx) file containing manual peeling data for processing and analysis
        </p>
      </div>

      {/* Expected Working Days Configuration */}
      <div className="mb-8">
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold mb-2 flex items-center">
                <MdInfo className="mr-2 text-2xl" />
                Monthly Working Days Configuration
              </h2>
              <p className="text-blue-100 text-sm">
                Set the expected working days for performance tracking and calculations
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {/* Month Selection */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Select Month
              </label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full px-4 py-3 bg-white/20 backdrop-blur-sm text-white placeholder-white/70 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 font-medium"
              />
              <p className="text-xs text-blue-100 mt-2">
                {new Date(selectedMonth + '-01').toLocaleDateString('en-US', { 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>
            </div>

            {/* Expected Working Days Input */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Expected Working Days
              </label>
              <input
                type="number"
                min="1"
                max="31"
                value={expectedWorkingDays}
                onChange={(e) => setExpectedWorkingDays(parseInt(e.target.value) || 0)}
                className="w-full px-4 py-3 bg-white/20 backdrop-blur-sm text-white placeholder-white/70 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 font-medium text-lg"
                placeholder="Enter expected days"
              />
              <p className="text-xs text-blue-100 mt-2">
                Typically 22-26 working days per month (excluding weekends and holidays)
              </p>
            </div>
          </div>

          {/* Summary Display */}
          <div className="mt-6 bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <MdCheckCircle className="text-green-300 mr-2 text-xl" />
                <span className="font-semibold">Configuration Set:</span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <span className="text-2xl font-bold">{expectedWorkingDays}</span>
                  <span className="text-sm ml-2">working days in</span>
                  <span className="text-lg font-semibold ml-2">
                    {new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </span>
                </div>
                <button
                  onClick={handleSaveWorkingDaysConfig}
                  disabled={isLoading || !selectedMonth || !expectedWorkingDays}
                  className="px-6 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors flex items-center font-semibold shadow-lg disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                  <MdSave className="mr-2" />
                  Save Config
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Set Target Section - HIDDEN */}
      {/* <div className="mb-8">
        <div className="bg-green-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold mb-2 flex items-center">
                <MdCheckCircle className="mr-2 text-2xl" />
                Set Target
              </h2>
              <p className="text-white text-sm">
                Configure the desired performance target for workers
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Target Value (kg)
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={targetValue}
                  onChange={(e) => setTargetValue(parseFloat(e.target.value) || 0)}
                  className="w-full px-4 py-3 bg-white/20 backdrop-blur-sm text-white placeholder-white/70 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 font-medium text-lg pr-12"
                  placeholder="Enter target"
                />
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white font-semibold">
                  kg
                </span>
              </div>
              <p className="text-xs text-white/90 mt-2">
                Daily output target per worker
              </p>
            </div>

            <div className="flex items-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 flex-1">
                <div className="text-center">
                  <p className="text-sm text-white/90 mb-2">Current Target</p>
                  <p className="text-4xl font-bold text-white">{targetValue.toFixed(1)} <span className="text-2xl">kg</span></p>
                  <button
                    onClick={handleSaveTarget}
                    disabled={isLoading || !targetValue || targetValue <= 0}
                    className="mt-4 px-6 py-2 bg-white text-green-700 rounded-lg hover:bg-green-50 transition-colors flex items-center font-semibold shadow-lg mx-auto disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
                  >
                    <MdSave className="mr-2" />
                    Save Target
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> */}

      {/* Upload Section */}
      <div className="mb-8">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <MdUpload className="mr-2 text-blue-500 text-3xl" />
            Upload Excel File
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Drag and Drop Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <MdCloudUpload className="text-4xl text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">
                Drag and drop your Excel file here
              </p>
              <p className="text-sm text-gray-500 mb-4">or</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center mx-auto"
              >
                <MdFileUpload className="mr-2" />
                Choose File
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                onChange={handleFileInputChange}
                className="hidden"
              />
            </div>

            
            {/* File Status */}
            <div className="space-y-4">
              {/* File Info */}
              {uploadedFile ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <MdCheckCircle className="text-green-500 mr-2" />
                      <span className="text-sm text-green-700 font-medium">{uploadedFile.name}</span>
                    </div>
                    <span className="text-xs text-green-600 font-semibold">
                      {(uploadedFile.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-700 text-center">
                    <MdInfo className="inline mr-1" />
                    No file selected yet
                  </p>
                </div>
              )}
            </div>

            {/* Expected Format Info */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg h-full">
              <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
                <MdInfo className="mr-2 text-xl" />
                Expected Format
              </h3>
              <ul className="text-sm text-blue-700 space-y-2">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>NO#, TransactionDate</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>FirstName, LastName</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>StaffNumber, CardNumber</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Product</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>TareWeight, GrossWeight, NetWeight</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Section */}
      <div className="mb-8">
          {showPreview ? (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                  <MdTableChart className="mr-2 text-green-500 text-3xl" />
                  Data Preview
                </h2>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    Showing first 5 rows of uploaded data
                  </span>
                  <div className="flex gap-3">
                    <button
                      onClick={handleClearData}
                      disabled={isUploading || isSubmitted}
                      className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors flex items-center disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      <MdClose className="mr-2" />
                      Clear
                    </button>
                    {!isSubmitted ? (
                      <button
                        onClick={handleSubmit}
                        disabled={isUploading}
                        className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center disabled:bg-green-300 disabled:cursor-not-allowed min-w-[140px] justify-center"
                      >
                        {isUploading ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <MdUpload className="mr-2" />
                            Submit
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        className="bg-green-600 text-white px-6 py-2 rounded-lg flex items-center cursor-default"
                      >
                        <MdCheckCircle className="mr-2" />
                        Submitted
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Upload Progress Bar */}
              {isUploading && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-700">
                      Uploading data to server...
                    </span>
                    <span className="text-sm font-bold text-blue-700">
                      {uploadProgress}%
                    </span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-3 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-300 ease-out flex items-center justify-end"
                      style={{ width: `${uploadProgress}%` }}
                    >
                      <div className="animate-pulse bg-white/30 h-full w-20 absolute"></div>
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Please wait while we process your file...
                  </p>
                </div>
              )}

              {/* Success Message */}
              {isSubmitted && !isUploading && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <MdCheckCircle className="text-green-500 mr-3 text-2xl" />
                    <div>
                      <p className="text-sm font-semibold text-green-700">
                        Upload Completed Successfully!
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        Your data has been uploaded and processed. Redirecting...
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h3 className="font-semibold text-red-800 mb-2 flex items-center">
                    <MdError className="mr-2" />
                    Validation Errors ({validationErrors.length})
                  </h3>
                  <div className="max-h-32 overflow-y-auto">
                    {validationErrors.map((error, index) => (
                      <p key={index} className="text-sm text-red-700">{error}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Data Table */}
              <div className="mb-4 flex items-center justify-between">
                <p className="text-xs text-gray-500 italic flex items-center">
                  <span className="mr-2">💡</span>
                  <span>Scroll horizontally to view all columns →</span>
                </p>
                <p className="text-xs text-blue-600 font-medium">
                  {headers.length} columns from Excel file
                </p>
              </div>
              
              {/* Scrollable table container */}
              <div className="relative w-full">
                <div className="w-full overflow-x-scroll overflow-y-visible border border-gray-200 rounded-lg shadow-sm bg-white">
                  <table className="w-full border-collapse table-auto">
                    <thead>
                      <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                        <th className="border-b-2 border-gray-300 px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-100 z-20 min-w-[80px] whitespace-nowrap">
                          Row
                        </th>
                        {headers.map((header, index) => (
                          <th key={index} className="border-b-2 border-gray-300 px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider min-w-[150px] whitespace-nowrap">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {csvData.map((row, rowIndex) => (
                        <tr key={rowIndex} className={`${row._isValid ? 'hover:bg-blue-50' : 'bg-red-50 hover:bg-red-100'} transition-colors`}>
                          <td className="border-b border-gray-200 px-4 py-3 text-sm sticky left-0 bg-white z-10 min-w-[80px] whitespace-nowrap">
                            <div className="flex items-center font-medium">
                              {row._isValid ? (
                                <MdCheckCircle className="text-green-500 mr-2 flex-shrink-0" size={16} />
                              ) : (
                                <MdError className="text-red-500 mr-2 flex-shrink-0" size={16} />
                              )}
                              <span className="font-semibold text-gray-700">{row._rowNumber}</span>
                            </div>
                          </td>
                          {headers.map((header, headerIndex) => (
                            <td key={headerIndex} className="border-b border-gray-200 px-4 py-3 text-sm min-w-[150px] whitespace-nowrap">
                              <span className={!row._isValid ? 'text-red-600 font-medium' : 'text-gray-700'}>
                                {row[header] !== null && row[header] !== undefined && row[header] !== '' ? String(row[header]) : '-'}
                              </span>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Scroll indicator */}
                <div className="mt-2 text-center">
                  <p className="text-xs text-gray-400 italic">← Drag or scroll to see all {headers.length} columns →</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
              <MdVisibility className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                No Data Preview Available
              </h3>
              <p className="text-gray-500">
                Upload an Excel (.xlsx) file to see the uploaded data preview
              </p>
            </div>
          )}
      </div>

      {/* Loading Overlay */}
      {isLoading && <UsibrasLoader />}

      {/* Invalid Cards Modal */}
      {showInvalidCardsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-2xl rounded-xl bg-white p-6">
            {/* Modal Header */}
            <div className="mb-6 flex items-center justify-between border-b border-gray-200 pb-4">
              <div>
                <h3 className="text-xl font-bold text-red-700 flex items-center">
                  <MdError className="mr-2 text-2xl" />
                  Invalid Card Numbers Found
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  The following card numbers do not exist in the manual peeling workers table
                </p>
              </div>
              <button
                onClick={() => setShowInvalidCardsModal(false)}
                className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <MdClose className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="mb-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-700">
                  <strong>{invalidCards.length}</strong> invalid card number{invalidCards.length !== 1 ? 's' : ''} found. 
                  Please ensure all workers are registered in the manual_peeling_workers table before importing their weighing records.
                </p>
              </div>
              
              <div className="max-h-64 overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {invalidCards.map((cardNumber, index) => (
                    <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Card #{cardNumber}</span>
                        <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full">
                          Invalid
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end">
              <button
                onClick={() => setShowInvalidCardsModal(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Dates Modal */}
      {showDuplicateDatesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-2xl rounded-xl bg-white p-6">
            {/* Modal Header */}
            <div className="mb-6 flex items-center justify-between border-b border-gray-200 pb-4">
              <div>
                <h3 className="text-xl font-bold text-orange-700 flex items-center">
                  <MdWarning className="mr-2 text-2xl" />
                  Duplicate Transaction Dates Found
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  The following transaction dates already exist in the database
                </p>
              </div>
              <button
                onClick={() => setShowDuplicateDatesModal(false)}
                className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <MdClose className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="mb-6">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-orange-700">
                  <strong>{duplicateDates.length}</strong> duplicate date{duplicateDates.length !== 1 ? 's' : ''} found. 
                  Please ensure all transaction dates in the import file are unique and don't exist in the database.
                </p>
              </div>
              
              <div className="max-h-64 overflow-y-auto">
                <div className="space-y-3">
                  {duplicateDates.map((date, index) => (
                    <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium text-gray-700">
                            {new Date(date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                          <p className="text-xs text-gray-500 mt-1">Date: {date}</p>
                        </div>
                        <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                          Duplicate
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end">
              <button
                onClick={() => setShowDuplicateDatesModal(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      {toast.show && (
        <UsibrasToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'info' })}
        />
      )}
    </div>
  );
};

export default AddManPeelingData;
